<script setup lang="ts">
import type {ProjectData} from "~/types/ProjectData";
import {chaptersCountLabel} from "~/utils/project";
import {userHasRole} from "~/utils/user";
import {useMyFavorites} from "~/composables/useMyFavorites";
import {ProjectStatus} from "~/types/ProjectStatus";
import Tooltip from "~/components/Tooltip.vue";
import {spiceLevelLabel} from "~/composables/useSpiceLevels";

const props = withDefaults(defineProps<{
  project: ProjectData
  refreshNuxtData?: boolean
  responsiveLayout?: "compact" | "detail"
}>(), {
  responsiveLayout: "compact",
});

const appUser = useAppUser();
const {handleProjectFavorite} = useMyFavorites();

const canRead = computed(() => !!appUser.value && (userHasRole(appUser.value, "reader") || userHasRole(appUser.value, "author")));

const {
  isFavorite,
  isPending: isFavoritePending,
  toggleFavorite,
} = handleProjectFavorite(props.project.is_favorite, props.refreshNuxtData ? () => refreshNuxtData() : undefined as any);

watch(() => props.project.is_favorite, (newValue) => {
  if (newValue !== undefined) {
    isFavorite.value = newValue;
  }
});
</script>

<template>
  <div class="border rounded p-4 md:p-6 bg-white hover:shadow-lg transition-shadow">
    <div class="relative grid lg:flex lg:flex-col lg:justify-between lg:h-full gap-4"
         :class="responsiveLayout === 'detail' ? 'grid-cols-[25%_1fr] md:grid-cols-[33%_1fr]':'grid-cols-1'">

      <div class="absolute right-0 top-0" :class="responsiveLayout === 'detail' ? 'block':'hidden lg:block'" v-if="appUser && canRead">
        <Tooltip :text="isFavorite ? 'Retirer des favoris':'Ajouter aux favoris'">
          <UButton
              :icon="isFavorite ? 'i-custom-bookmark-solid' : 'i-heroicons-bookmark'"
              variant="ghost"
              @click.prevent="toggleFavorite(project.uuid)"
              :disabled="isFavoritePending"
              :ui="{leadingIcon:'fill-yellow-400'}"
          />
        </Tooltip>
      </div>

      <div :class="responsiveLayout === 'detail' ? '':'col-span-2'">
        <NuxtLink :to="`/project/${project.uuid}`">
          <div v-if="project.image">
            <img :src="project.image?.thumbnail" :alt="project.title" class="image-project w-full lg:w-[145px]"></img>
          </div>
          <div v-else>
            <USkeleton class="image-project lg:w-[145px]"/>
          </div>
        </NuxtLink>
      </div>

      <div :class="responsiveLayout === 'detail' ? 'block':'hidden lg:block lg:grow'">
        <h3 class="mb-2 pr-8 lg:pr-0">
          <NuxtLink :to="`/project/${project.uuid}`">{{ project.title }}</NuxtLink>
        </h3>
        <NuxtLink :to="`/project/${project.uuid}`">
          <div>{{ project.pitch }}</div>
        </NuxtLink>
      </div>


      <div class="col-span-2 row-start-2 flex justify-between" :class="responsiveLayout === 'detail' ? 'flex':'hidden lg:flex'">
        <div class="text-sm space-x-2 flex items-center text-gray-300">
          <NuxtLink :to="`/author/${project.authors?.[0]?.uuid}`" class="font-bold text-gray-400">{{ project.authors?.[0]?.name }}</NuxtLink>
          <span>{{ chaptersCountLabel(project.public_chapters_count) }}</span>
          <Tooltip v-if="project.status === ProjectStatus.InProgress" text="Histoire en cours">
            <UIcon name="i-heroicons-clock"></UIcon>
          </Tooltip>
          <Tooltip v-if="project.status === ProjectStatus.Complete" text="Histoire terminée">
            <UIcon name="i-heroicons-check-circle"></UIcon>
          </Tooltip>
        </div>
        <div class="text-xs whitespace-nowrap flex items-center gap-1">
          <Tooltip :text="spiceLevelLabel(project.spice_level-1)">
            <div>
              <UIcon v-if="project.spice_level > 0" v-for="i in project.spice_level" name="i-custom-spicy" class="size-4"></UIcon>
            </div>
          </Tooltip>
        </div>
      </div>

      <div class="col-span-2 row-start-3 flex flex-wrap gap-x-2 gap-y-1 justify-between items-center border-t border-dashed border-gray-300 pt-2">
        <BadgeGenre v-if="project.genres?.[0]"
                    :class="responsiveLayout === 'detail' ? 'block':'hidden lg:block'"
                    :genre="project.genres[0]"/>
        <ul class="flex gap-x-1.5" v-if="project.top_emotions?.length > 0">
          <li v-for="emotion in project.top_emotions" class="flex items-center gap-x-1">
            <Tooltip :text="emotion.title">
              <UIcon :name="emotion.icon"/>
            </Tooltip>
            <span class="text-sm">{{ emotion.count }}</span>
          </li>
        </ul>
        <div v-else>&nbsp;</div>
      </div>

    </div>
  </div>
</template>
